import { ThemeProvider } from "@/contexts/ThemeContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import AppRouter from "@/routes/index";

/**
 * Root application component.
 *
 * Composes the global providers (Theme, Auth, Notification) around the router.
 * The `AppRouter` from `routes/index.tsx` uses `createBrowserRouter` +
 * `RouterProvider`, which is the canonical routing setup for the app.
 *
 * Note: `CommandPalette` is rendered inside `MainLayout` and `AdminLayout`
 * so that it has access to the router context (`useNavigate`, etc.).
 *
 * Provider order matters:
 * 1. ErrorBoundary — catches rendering errors at the top level
 * 2. ThemeProvider — provides dark/light/system theme context
 * 3. AuthProvider — manages authentication state, JWT tokens, user profile
 * 4. NotificationProvider — manages notification state, polling, unread count
 *    (must be inside AuthProvider since it depends on auth state)
 */
export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system">
        <AuthProvider>
          <NotificationProvider>
            <AppRouter />
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
